/* ==========================================================================
   The Dog Blueprint — landing-page + product-page behaviour
   File: assets/tdb-bundles.js

   FAST NATIVE SHOPIFY CART VERSION

   - Uses Shopify standard storefront actions
   - Preloads the AJAX cart in the background
   - Uses real AJAX line keys for existing cart lines
   - Adds/removes/replaces in one native updateCart call
   - Keeps Whisper responsible for refreshing/opening the drawer
   - Routes TDB product-page Add to Cart buttons through the same fast flow
   ========================================================================== */

(function () {
  'use strict';
  if (window.__tdbBundlesLoaded) return;
  window.__tdbBundlesLoaded = true;

  var reducedMotion = window
    .matchMedia('(prefers-reduced-motion: reduce)')
    .matches;

  var shopifyRoot =
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  if (shopifyRoot.slice(-1) !== '/') {
    shopifyRoot += '/';
  }

  var cartJsonUrl = shopifyRoot + 'cart.js';

  var shopifyActionsPromise = null;
  var cartSnapshot = null;
  var cartSnapshotPromise = null;
  var cartSnapshotRefreshTimer = null;

  var TDB_ESSENTIAL_HANDLE =
    'the-dog-nutrition-system';

  var TDB_ULTIMATE_HANDLE =
    'the-ultimate-dog-nutrition-system';

  var TDB_LONGEVITY_HANDLE =
    'dog-longevity-blueprint';

  var TDB_FEEDING_HANDLE =
    'done-for-you-feeding-plan';

  var TDB_BOTH_UPSELL_HANDLE =
    'done-for-you-feeding-plan-longevity-guide';

  var TDB_PRODUCT_HANDLES = [
    TDB_ESSENTIAL_HANDLE,
    TDB_ULTIMATE_HANDLE,
    TDB_LONGEVITY_HANDLE,
    TDB_FEEDING_HANDLE,
    TDB_BOTH_UPSELL_HANDLE
  ];

  function parseList(value) {
    if (!value) return [];

    return value
      .split(',')
      .map(function (valuePart) {
        return valuePart.trim();
      })
      .filter(Boolean);
  }

  function isManagedTDBProduct(handle) {
    return (
      TDB_PRODUCT_HANDLES.indexOf(
        String(handle || '')
      ) !== -1
    );
  }

  function getReplaceHandlesForProduct(handle) {
    handle = String(handle || '');

    if (handle === TDB_ESSENTIAL_HANDLE) {
      return [TDB_ULTIMATE_HANDLE];
    }

    if (handle === TDB_ULTIMATE_HANDLE) {
      return [TDB_ESSENTIAL_HANDLE];
    }

    if (handle === TDB_BOTH_UPSELL_HANDLE) {
      return [
        TDB_LONGEVITY_HANDLE,
        TDB_FEEDING_HANDLE
      ];
    }

    if (
      handle === TDB_LONGEVITY_HANDLE ||
      handle === TDB_FEEDING_HANDLE
    ) {
      return [TDB_BOTH_UPSELL_HANDLE];
    }

    return [];
  }

  function getStatusElement(button) {
    var id = button.dataset.statusTarget;

    return id
      ? document.getElementById(id)
      : null;
  }

  function getConfirmationElement(button) {
    var id = button.dataset.confirmTarget;

    return id
      ? document.getElementById(id)
      : null;
  }

  function restoreButton(
    button,
    originalHTML
  ) {
    button.innerHTML = originalHTML;
    button.disabled = false;

    button.removeAttribute(
      'aria-busy'
    );

    button.removeAttribute(
      'data-tdb-busy'
    );
  }

  function setStatus(
    element,
    message,
    state
  ) {
    if (!element) return;

    element.textContent =
      message || '';

    if (state) {
      element.setAttribute(
        'data-state',
        state
      );
    } else {
      element.removeAttribute(
        'data-state'
      );
    }
  }

  function getShopifyActions() {
    if (shopifyActionsPromise) {
      return shopifyActionsPromise;
    }

    shopifyActionsPromise =
      new Promise(
        function (
          resolve,
          reject
        ) {
          var attempts = 0;

          function check() {
            var actions =
              window.Shopify &&
              window.Shopify.actions;

            if (
              actions &&
              typeof actions.updateCart ===
                'function' &&
              typeof actions.openCart ===
                'function'
            ) {
              resolve(actions);
              return;
            }

            attempts += 1;

            if (attempts >= 40) {
              shopifyActionsPromise =
                null;

              reject(
                new Error(
                  'Shopify cart actions did not load. Please refresh the page.'
                )
              );

              return;
            }

            window.setTimeout(
              check,
              50
            );
          }

          check();
        }
      );

    return shopifyActionsPromise;
  }

  function makeEmptySnapshot() {
    return new Map();
  }

  function buildCartSnapshot(cart) {
    var snapshot =
      makeEmptySnapshot();

    if (
      !cart ||
      !Array.isArray(cart.items)
    ) {
      return snapshot;
    }

    cart.items.forEach(
      function (item) {
        if (
          !item ||
          item.variant_id == null ||
          !item.key
        ) {
          return;
        }

        var variantId =
          String(
            item.variant_id
          );

        var lines =
          snapshot.get(
            variantId
          ) || [];

        lines.push({
          key:
            String(item.key),

          quantity:
            Number(
              item.quantity || 0
            ),

          handle:
            String(
              item.handle || ''
            )
        });

        snapshot.set(
          variantId,
          lines
        );
      }
    );

    return snapshot;
  }

  function readCartSnapshot() {
    return fetch(
      cartJsonUrl,
      {
        method: 'GET',

        headers: {
          Accept:
            'application/json'
        },

        credentials:
          'same-origin',

        cache:
          'no-store'
      }
    )
      .then(
        function (response) {
          if (!response.ok) {
            throw new Error(
              'The cart state could not be loaded.'
            );
          }

          return response.json();
        }
      )
      .then(
        function (cart) {
          cartSnapshot =
            buildCartSnapshot(
              cart
            );

          return cartSnapshot;
        }
      );
  }

  function ensureCartSnapshot() {
    if (cartSnapshot) {
      return Promise.resolve(
        cartSnapshot
      );
    }

    if (!cartSnapshotPromise) {
      cartSnapshotPromise =
        readCartSnapshot()
          .catch(
            function (error) {
              console.warn(
                '[TDB] Cart preload failed.',
                error
              );

              cartSnapshot =
                makeEmptySnapshot();

              return cartSnapshot;
            }
          )
          .finally(
            function () {
              cartSnapshotPromise =
                null;
            }
          );
    }

    return cartSnapshotPromise;
  }

  function refreshCartSnapshot() {
    cartSnapshot = null;
    cartSnapshotPromise = null;

    return ensureCartSnapshot();
  }

  function scheduleCartSnapshotRefresh(
    delay
  ) {
    if (
      cartSnapshotRefreshTimer
    ) {
      window.clearTimeout(
        cartSnapshotRefreshTimer
      );
    }

    cartSnapshotRefreshTimer =
      window.setTimeout(
        function () {
          cartSnapshotRefreshTimer =
            null;

          refreshCartSnapshot()
            .catch(
              function () {
              }
            );
        },

        typeof delay ===
          'number'
          ? delay
          : 0
      );
  }

  function buildCartLines(
    variantId,
    replaceIds,
    replaceHandles,
    snapshot
  ) {
    var selectedId =
      String(variantId);

    var lines = [];

    var usedKeys =
      new Set();

    replaceIds.forEach(
      function (id) {
        var replaceId =
          String(id);

        if (
          replaceId ===
          selectedId
        ) {
          return;
        }

        var existing =
          snapshot.get(
            replaceId
          ) || [];

        existing.forEach(
          function (line) {
            if (
              !line.key ||
              usedKeys.has(
                line.key
              )
            ) {
              return;
            }

            lines.push({
              id:
                line.key,

              quantity: 0
            });

            usedKeys.add(
              line.key
            );
          }
        );
      }
    );

    if (
      replaceHandles.length
    ) {
      snapshot.forEach(
        function (
          existingLines
        ) {
          existingLines.forEach(
            function (line) {
              if (
                !line.key ||
                usedKeys.has(
                  line.key
                ) ||
                replaceHandles
                  .indexOf(
                    String(
                      line.handle ||
                        ''
                    )
                  ) === -1
              ) {
                return;
              }

              lines.push({
                id:
                  line.key,

                quantity: 0
              });

              usedKeys.add(
                line.key
              );
            }
          );
        }
      );
    }

    var selectedLines =
      snapshot.get(
        selectedId
      ) || [];

    if (
      selectedLines.length
    ) {
      selectedLines.forEach(
        function (
          line,
          index
        ) {
          if (
            !line.key ||
            usedKeys.has(
              line.key
            )
          ) {
            return;
          }

          lines.push({
            id:
              line.key,

            quantity:
              index === 0
                ? 1
                : 0
          });

          usedKeys.add(
            line.key
          );
        }
      );
    } else {
      lines.push({
        merchandiseId:
          selectedId,

        quantity: 1
      });
    }

    return lines;
  }

  function updateCartBubble(
    cart
  ) {
    if (!cart) return;

    var count =
      Number(
        cart.totalQuantity ||
          0
      );

    document
      .querySelectorAll(
        '.cart-bubble'
      )
      .forEach(
        function (bubble) {
          var countElement =
            bubble.querySelector(
              '.cart-bubble__text-count'
            );

          if (!countElement) {
            return;
          }

          if (count > 0) {
            bubble.classList.remove(
              'visually-hidden'
            );

            countElement
              .classList
              .remove(
                'hidden'
              );

            countElement
              .textContent =
                count > 99
                  ? '99+'
                  : String(
                      count
                    );

            if (
              count <= 99
            ) {
              bubble.setAttribute(
                'data-maintain-ratio',
                ''
              );
            } else {
              bubble.removeAttribute(
                'data-maintain-ratio'
              );
            }
          } else {
            bubble.classList.add(
              'visually-hidden'
            );

            countElement
              .classList
              .add(
                'hidden'
              );

            countElement
              .textContent =
                '';
          }
        }
      );
  }

  function handleAdd(button) {
    if (
      button.disabled ||
      button.getAttribute(
        'aria-disabled'
      ) === 'true' ||
      button.getAttribute(
        'data-tdb-busy'
      ) === 'true'
    ) {
      return;
    }

    var variantId =
      button.dataset.variantId;

    var originalHTML =
      button.innerHTML;

    var statusElement =
      getStatusElement(
        button
      );

    var confirmationElement =
      getConfirmationElement(
        button
      );

    if (!variantId) {
      setStatus(
        statusElement,

        "This product isn't configured correctly.",

        'error'
      );

      return;
    }

    var replaceIds =
      parseList(
        button.dataset
          .replaceIds || ''
      );

    var replaceHandles =
      parseList(
        button.dataset
          .replaceHandles || ''
      );

    button.disabled = true;

    button.setAttribute(
      'aria-busy',
      'true'
    );

    button.setAttribute(
      'data-tdb-busy',
      'true'
    );

    button.innerHTML =
      '<span class="tdb-spinner" aria-hidden="true"></span>' +
      '<span class="tdb-visually-hidden">Adding to cart</span>';

    setStatus(
      statusElement,
      '',
      null
    );

    if (
      confirmationElement
    ) {
      confirmationElement
        .hidden = true;
    }

    Promise.all([
      getShopifyActions(),
      ensureCartSnapshot()
    ])

      .then(
        function (values) {
          var actions =
            values[0];

          var snapshot =
            values[1];

          var lines =
            buildCartLines(
              variantId,
              replaceIds,
              replaceHandles,
              snapshot
            );

          return actions
            .updateCart(
              {
                lines:
                  lines
              },

              {
                event: {
                  context:
                    button.closest(
                      '.tdb-cart-upsells'
                    )
                      ? 'dialog'
                      : 'product'
                }
              }
            )

            .then(
              function (
                result
              ) {
                return {
                  actions:
                    actions,

                  result:
                    result
                };
              }
            );
        }
      )

      .then(
        function (data) {
          var actions =
            data.actions;

          var result =
            data.result ||
            {};

          if (
            result.userErrors &&
            result.userErrors
              .length
          ) {
            throw new Error(
              result
                .userErrors[0]
                .message ||
              'The product could not be added.'
            );
          }

          if (
            result.cart
          ) {
            updateCartBubble(
              result.cart
            );
          }

          scheduleCartSnapshotRefresh(
            0
          );

          restoreButton(
            button,
            originalHTML
          );

          button.textContent =
            'Added ✓';

          if (
            confirmationElement
          ) {
            confirmationElement
              .hidden =
                false;
          } else {
            setStatus(
              statusElement,

              'Added to your cart.',

              'success'
            );
          }

          return actions
            .openCart()

            .catch(
              function (
                error
              ) {
                console.warn(
                  '[TDB] Cart could not auto-open.',
                  error
                );
              }
            );
        }
      )

      .then(
        function () {
          window.setTimeout(
            function () {
              button.innerHTML =
                originalHTML;
            },

            1200
          );
        }
      )

      .catch(
        function (error) {
          console.warn(
            '[TDB] Add to cart failed.',
            error
          );

          scheduleCartSnapshotRefresh(
            0
          );

          restoreButton(
            button,
            originalHTML
          );

          setStatus(
            statusElement,

            error.message ||
              "Couldn't add that to your cart. Please try again.",

            'error'
          );
        }
      );
  }

  function getManagedProductFormFromElement(
    element
  ) {
    if (!element) {
      return null;
    }

    var productForm =
      element.closest(
        'product-form-component[data-tdb-digital="true"]'
      );

    if (!productForm) {
      return null;
    }

    if (
      !isManagedTDBProduct(
        productForm.dataset
          .tdbProductHandle ||
          ''
      )
    ) {
      return null;
    }

    return productForm;
  }

  function getProductFormVariantId(
    productForm
  ) {
    if (!productForm) {
      return '';
    }

    var input =
      productForm.querySelector(
        'input[name="id"][ref="variantId"], input[name="id"]'
      );

    return (
      input &&
      input.value
    )
      ? String(
          input.value
        )
      : '';
  }

  function prepareNativeProductButton(
    button,
    productForm
  ) {
    if (
      !button ||
      !productForm
    ) {
      return false;
    }

    var variantId =
      getProductFormVariantId(
        productForm
      );

    var handle =
      productForm.dataset
        .tdbProductHandle ||
      '';

    if (
      !variantId ||
      !isManagedTDBProduct(
        handle
      )
    ) {
      return false;
    }

    button.dataset
      .variantId =
        variantId;

    button.dataset
      .replaceHandles =
        getReplaceHandlesForProduct(
          handle
        ).join(',');

    return true;
  }

  function findManagedProductFormForSticky(
    sticky
  ) {
    if (!sticky) {
      return null;
    }

    var productId =
      String(
        sticky.dataset
          .productId || ''
      );

    var forms =
      document.querySelectorAll(
        'product-form-component[data-tdb-digital="true"]'
      );

    for (
      var index = 0;
      index <
        forms.length;
      index += 1
    ) {
      var form =
        forms[index];

      if (
        String(
          form.dataset
            .productId ||
            ''
        ) ===
          productId &&
        isManagedTDBProduct(
          form.dataset
            .tdbProductHandle ||
            ''
        )
      ) {
        return form;
      }
    }

    return null;
  }

  document.addEventListener(
    'click',

    function (event) {
      var target =
        event.target;

      if (
        !target ||
        typeof target.closest !==
          'function'
      ) {
        return;
      }

      var nativeAddButton =
        target.closest(
          'button.add-to-cart-button[name="add"][type="submit"]'
        );

      if (
        nativeAddButton
      ) {
        var productForm =
          getManagedProductFormFromElement(
            nativeAddButton
          );

        if (
          productForm &&
          prepareNativeProductButton(
            nativeAddButton,
            productForm
          )
        ) {
          event.preventDefault();

          event
            .stopImmediatePropagation();

          handleAdd(
            nativeAddButton
          );

          return;
        }
      }

      var stickyButton =
        target.closest(
          'sticky-add-to-cart .sticky-add-to-cart__button'
        );

      if (!stickyButton) {
        return;
      }

      var sticky =
        stickyButton.closest(
          'sticky-add-to-cart'
        );

      var stickyProductForm =
        findManagedProductFormForSticky(
          sticky
        );

      if (
        !stickyProductForm ||
        !prepareNativeProductButton(
          stickyButton,
          stickyProductForm
        )
      ) {
        return;
      }

      event.preventDefault();

      event
        .stopImmediatePropagation();

      handleAdd(
        stickyButton
      );
    },

    true
  );

  document.addEventListener(
    'click',

    function (event) {
      var target =
        event.target;

      if (
        !target ||
        typeof target.closest !==
          'function'
      ) {
        return;
      }

      var button =
        target.closest(
          '[data-tdb-add]'
        );

      if (!button) {
        return;
      }

      event.preventDefault();

      event.stopPropagation();

      handleAdd(
        button
      );
    }
  );

  document.addEventListener(
    'shopify:cart:lines-update',

    function (event) {
      if (
        event &&
        event.promise &&
        typeof event.promise.then ===
          'function'
      ) {
        event.promise

          .then(
            function () {
              scheduleCartSnapshotRefresh(
                0
              );
            }
          )

          .catch(
            function () {
              scheduleCartSnapshotRefresh(
                0
              );
            }
          );

        return;
      }

      scheduleCartSnapshotRefresh(
        500
      );
    }
  );

  function initStickyBar() {
    var bar =
      document.querySelector(
        '[data-tdb-sticky]'
      );

    if (
      !bar ||
      bar.dataset
        .tdbStickyBound ===
        'true'
    ) {
      return;
    }

    var startElement =
      document.getElementById(
        bar.dataset
          .startAfter || ''
      );

    var endElement =
      document.getElementById(
        bar.dataset
          .hideAt || ''
      );

    if (!startElement) {
      return;
    }

    bar.dataset
      .tdbStickyBound =
        'true';

    var pastStart =
      false;

    var atEnd =
      false;

    function refresh() {
      bar.classList.toggle(
        'is-visible',

        pastStart &&
          !atEnd
      );
    }

    if (
      !(
        'IntersectionObserver'
        in window
      )
    ) {
      bar.classList.add(
        'is-visible'
      );
    } else {
      new IntersectionObserver(
        function (entries) {
          var entry =
            entries[0];

          pastStart =
            !entry.isIntersecting &&
            entry
              .boundingClientRect
              .top < 0;

          refresh();
        },

        {
          threshold: 0
        }
      ).observe(
        startElement
      );

      if (endElement) {
        new IntersectionObserver(
          function (
            entries
          ) {
            atEnd =
              entries[0]
                .isIntersecting;

            refresh();
          },

          {
            threshold: 0
          }
        ).observe(
          endElement
        );
      }
    }

    var backButton =
      bar.querySelector(
        '[data-tdb-scroll-to]'
      );

    if (backButton) {
      backButton.addEventListener(
        'click',

        function () {
          startElement
            .scrollIntoView(
              {
                behavior:
                  reducedMotion
                    ? 'auto'
                    : 'smooth',

                block:
                  'start'
              }
            );
        }
      );
    }
  }

  function initReveal() {
    var items =
      document.querySelectorAll(
        '.tdb-reveal:not(.is-in)'
      );

    if (!items.length) {
      return;
    }

    if (
      reducedMotion ||
      !(
        'IntersectionObserver'
        in window
      )
    ) {
      items.forEach(
        function (
          element
        ) {
          element.classList.add(
            'is-in'
          );
        }
      );

      return;
    }

    var observer =
      new IntersectionObserver(
        function (
          entries
        ) {
          entries.forEach(
            function (
              entry
            ) {
              if (
                !entry
                  .isIntersecting
              ) {
                return;
              }

              entry.target
                .classList.add(
                  'is-in'
                );

              observer
                .unobserve(
                  entry.target
                );
            }
          );
        },

        {
          rootMargin:
            '0px 0px -10% 0px',

          threshold:
            0.05
        }
      );

    items.forEach(
      function (
        element
      ) {
        observer.observe(
          element
        );
      }
    );
  }

  function initShowMore() {
    document
      .querySelectorAll(
        '[data-tdb-show-more]'
      )
      .forEach(
        function (
          button
        ) {
          if (
            button.dataset
              .tdbShowMoreBound ===
              'true'
          ) {
            return;
          }

          button.dataset
            .tdbShowMoreBound =
              'true';

          button
            .addEventListener(
              'click',

              function () {
                var grid =
                  document
                    .getElementById(
                      button
                        .dataset
                        .target
                    );

                if (!grid) {
                  return;
                }

                grid
                  .querySelectorAll(
                    '[hidden]'
                  )
                  .forEach(
                    function (
                      card
                    ) {
                      card.removeAttribute(
                        'hidden'
                      );
                    }
                  );

                button.remove();
              }
            );
        }
      );
  }

  function init() {
    initStickyBar();

    initReveal();

    initShowMore();

    ensureCartSnapshot();

    getShopifyActions()
      .catch(
        function () {
        }
      );
  }

  if (
    document.readyState ===
      'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }

  document.addEventListener(
    'shopify:section:load',
    init
  );
})();